import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { filterBy } from "@progress/kendo-data-query";
import { useNominalsBySubType, useAllNominals } from "services/nominalsService";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { v4 as uuidV4 } from "uuid";
import { Form, FormElement } from "@progress/kendo-react-form";
import { AmountFormatCell, formatDateForAPI } from "components/common/formatter";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { useActiveVats, useVats } from "services/vatService";
import { postMatterExpenses, useActiveExpenses } from "services/expenseService";
import { Checkbox, Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import SubmitButton from "components/common/SubmitButton";
import { getMatterDefaults } from "api";
import { toast } from "react-toastify";
import { CrossIcon } from "components/tabs/TabTitle";

const DateCell = (props) => {
  const { dataItem, field, onChange } = props;

  return (
    <td className="p-2">
      <div className={dataItem.hasDateError ? "validation-invalid" : ""}>
        <DatePicker
          value={dataItem.date}
          toggleButton={CalenderCustomToggleButton}
          onChange={(e) => {
            onChange({
              dataItem: dataItem,
              field: field,
              syntheticEvent: e,
              value: e.value,
            });
          }}
          placeholder="Select Date"
          width="100%"
        />
      </div>
    </td>
  );
};

const DetailsCell = (props) => {
  const { dataItem, field, onChange } = props;

  return (
    <td className="p-2">
      {dataItem.inEdit ? (
        <div className={dataItem.hasDetailsError ? "validation-invalid" : ""}>
          <Input
            type="text"
            value={dataItem[field] || ""}
            onChange={(e) => {
              onChange({
                dataItem: dataItem,
                field: field,
                syntheticEvent: e,
                value: e.value,
              });
            }}
            style={{ width: "100%" }}
          />
        </div>
      ) : (
        dataItem[field]
      )}
    </td>
  );
};

const ValueCellComponent = (props) => {
  const { dataItem, field, onChange } = props;

  return (
    <td className="p-2">
      <div className={dataItem.hasValueError ? "validation-invalid" : ""}>
        <NumericTextBox
          format={props.format || "n2"}
          className="mt-auto right-align"
          value={dataItem[field] !== undefined ? dataItem[field] : 0}
          onChange={(e) => {
            if (onChange) {
              onChange({
                dataItem: dataItem,
                field: field,
                syntheticEvent: e.syntheticEvent,
                value: e.value,
              });
            }
          }}
          spinners={false}
          style={{ width: "100%" }}
        />
      </div>
    </td>
  );
};

const findVatById = (vatCodeId, vats = []) =>
  vats.find((item) => String(item?.vatCodeId) === String(vatCodeId));

const resolveVatRate = (item, netAmount, vatAmount, vatRate) => {
  const explicitRate = Number(vatRate ?? item?.vatRate);
  if (Number.isFinite(explicitRate)) {
    return explicitRate;
  }
  const net = Number(netAmount);
  const vat = Number(vatAmount);
  if (Number.isFinite(net) && Number.isFinite(vat) && net !== 0) {
    return (vat / net) * 100;
  }
  return 0;
};

const isRowEmpty = (row) => {
  const hasDetails = Boolean(row.details?.trim());
  const hasValue = Number(row.netAmount) > 0;
  return !hasDetails && !hasValue;
};

const hasSelectedRows = (rows) => rows.some((row) => row.selected);

const getSelectionKey = (rows) =>
  rows.filter((row) => row.selected).map((row) => row.timeId).join(",");

const getSharedVatFromRows = (selectedRows) => {
  if (!selectedRows.length) return undefined;

  const firstVatId = selectedRows[0].vat?.vatCodeId ?? null;
  const allSame = selectedRows.every(
    (row) => (row.vat?.vatCodeId ?? null) === firstVatId,
  );

  return allSame ? selectedRows[0].vat ?? null : null;
};

const getSharedNominalFromRows = (selectedRows) => {
  if (!selectedRows.length) return undefined;

  const firstNominalRef = selectedRows[0].nominal?.nominalRef ?? null;
  const allSame = selectedRows.every(
    (row) => (row.nominal?.nominalRef ?? null) === firstNominalRef,
  );

  return allSame ? selectedRows[0].nominal ?? null : null;
};

const applyVatToRows = (rows, vat) =>
  rows.map((row) => {
    if (!row.selected) return row;
    if (!vat) {
      return {
        ...row,
        vat: null,
        vatAmount: 0,
        gross: Number(row.netAmount) || 0,
      };
    }
    const netAmount = Number(row.netAmount) || 0;
    const vatAmount = (vat.rate / 100) * netAmount;
    return {
      ...row,
      vat,
      vatAmount,
      gross: netAmount + vatAmount,
    };
  });

const applyNominalToRows = (rows, nominal) =>
  rows.map((row) => {
    if (!row.selected) return row;
    return { ...row, nominal: nominal || null };
  });

const applyVatToAllRows = (rows, vat) =>
  rows.map((row) => {
    if (!vat) {
      return {
        ...row,
        vat: null,
        vatAmount: 0,
        gross: Number(row.netAmount) || 0,
      };
    }
    const netAmount = Number(row.netAmount) || 0;
    const vatAmount = (vat.rate / 100) * netAmount;
    return {
      ...row,
      vat,
      vatAmount,
      gross: netAmount + vatAmount,
    };
  });

const applyNominalToAllRows = (rows, nominal) =>
  rows.map((row) => ({ ...row, nominal: nominal || null }));

const formatVatGridDisplay = (vat) => {
  if (!vat?.display) return "";
  const parts = vat.display.split(" - ").map((part) => part.trim());
  if (parts.length <= 1) return vat.display;
  return `${parts[0]} - ${parts[parts.length - 1]}`;
};

const formatNominalGridDisplay = (nominal) => nominal?.nominalRef || "";

const VatCodeDisplayCell = (props) => (
  <td className={`p-2 ${props.dataItem.hasVatError ? "validation-invalid" : ""}`}>
    {formatVatGridDisplay(props.dataItem.vat)}
  </td>
);

const NominalDisplayCell = (props) => (
  <td className={`p-2 ${props.dataItem.hasNominalError ? "validation-invalid" : ""}`}>
    {formatNominalGridDisplay(props.dataItem.nominal)}
  </td>
);

const ExpenseRequest = ({ matter, cancelEdit, onSubmitSuccess }) => {
  const initialData = [
    {
      timeId: uuidV4(),
      type: "Expense",
      date: new Date(),
      details: "",
      netAmount: 0,
      vatAmount: 0,
      gross: 0,
      vat: null,
      nominal: null,
      selected: false,
      inEdit: true,
    },
  ];

  const [data, setData] = React.useState(initialData);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const submitAttemptedRef = useRef(false);
  const [gridErrors, setGridErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showSelectExpense, setShowSelectExpense] = useState(false);
  const [expenseGridData, setExpenseGridData] = useState([]);
  const [vatvalue, setvatvalue] = useState();
  const [vatdata, setvatdata] = React.useState();
  const [nominal, setnominal] = useState();
  const [nominaldata, setnominaldata] = React.useState();
  const [matterDefaults, setMatterDefaults] = useState(null);
  const vatDefaultAppliedRef = useRef(false);
  const nominalDefaultAppliedRef = useRef(false);
  const prevSelectionKeyRef = useRef("");

  const { data: activevatsdata = [] } = useActiveVats();
  const { data: allVats = [] } = useVats();
  const { data: allNominals = [] } = useAllNominals();
  const { data: activeExpenses = [] } = useActiveExpenses();
  const { data: expenseNominals = [] } = useNominalsBySubType(
    "ProfitAndLoss_Expense",
    { excludeArchived: true },
  );

  const addNew = () => {
    const netAmount = 0;
    const vatAmount = vatvalue ? (vatvalue.rate / 100) * netAmount : 0;
    const newDataItem = {
      inEdit: true,
      timeId: uuidV4(),
      type: "Expense",
      date: new Date(),
      details: "",
      netAmount,
      vatAmount,
      gross: netAmount + vatAmount,
      vat: vatvalue || null,
      nominal: nominal || null,
      selected: false,
    };
    setData((prev) => [...prev, newDataItem]);
    if (gridErrors.length > 0) setGridErrors([]);
  };

  const removeRow = (item) => {
    setData((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((row) => row.timeId !== item.timeId);
    });
    if (gridErrors.length > 0) setGridErrors([]);
  };

  const DeleteCommandCell = (props) => (
    <td className="p-2">
      <CrossIcon
        disabled={data.length <= 1}
        onClick={() => removeRow(props.dataItem)}
        className="cursor-pointer"
      />
    </td>
  );

  const onChangenominal = useCallback((event) => {
    const nextNominal = event?.value || null;
    setnominal(nextNominal);
    setData((prev) => applyNominalToRows(prev, nextNominal));
  }, []);

  const onChangevat = useCallback((event) => {
    const nextVat = event.value ?? null;
    setvatvalue(nextVat);
    setData((prev) => applyVatToRows(prev, nextVat));
  }, []);

  const toggleRowSelected = (timeId, selected) => {
    setData((prev) =>
      prev.map((row) =>
        row.timeId === timeId ? { ...row, selected } : row,
      ),
    );
  };

  const toggleAllRowsSelected = (selected) => {
    setData((prev) => prev.map((row) => ({ ...row, selected })));
  };

  const filternominalData = (filter) => {
    const list = expenseNominals.slice();
    return filterBy(list, filter);
  };

  const filternominal = (event) => {
    setnominaldata(filternominalData(event.filter));
  };

  const filtervatData = (filter) => {
    const list = activevatsdata.slice();
    return filterBy(list, filter);
  };

  const filtervat = (event) => {
    setvatdata(filtervatData(event.filter));
  };

  const buildTemplateRow = (template) => {
    const netAmount = template.netAmount ?? 0;
    const vat =
      findVatById(template.vatCode, allVats) ||
      findVatById(template.vatCode, activevatsdata);
    const nominalMatch = expenseNominals.find(
      (item) => item.nominalRef === template.nominalRef,
    );
    const vatAmount = vat
      ? (vat.rate / 100) * netAmount
      : template.vatAmount ?? 0;

    return {
      inEdit: true,
      timeId: uuidV4(),
      type: "Expense",
      date: new Date(),
      details: template.details || "",
      netAmount,
      vatAmount,
      gross: netAmount + vatAmount,
      vat: vat || null,
      nominal: nominalMatch || null,
      selected: false,
    };
  };

  const applySelectedExpenseTemplates = () => {
    const selectedTemplates = expenseGridData.filter((item) => item.add === true);
    if (!selectedTemplates.length) return;

    const templateRows = selectedTemplates.map(buildTemplateRow);

    setData((prev) => {
      if (!prev.length) {
        return templateRows;
      }

      const lastRow = prev[prev.length - 1];
      if (isRowEmpty(lastRow)) {
        return [
          ...prev.slice(0, -1),
          { ...templateRows[0], timeId: lastRow.timeId },
          ...templateRows.slice(1),
        ];
      }

      return [...prev, ...templateRows];
    });

    closeSelectExpenseDialog();
    if (gridErrors.length > 0) setGridErrors([]);
  };

  const expenseGridItemChange = (e) => {
    setExpenseGridData((prev) =>
      prev.map((item) =>
        item.expenseId === e.dataItem.expenseId
          ? { ...item, add: e.value }
          : item,
      ),
    );
  };

  const getSelectedExpenseCount = () =>
    expenseGridData.filter((item) => item.add === true).length;

  const ExpenseSelectionHeaderCell = (props) => (
    <div className="d-flex align-items-center justify-content-center">
      <Checkbox
        style={{
          width: "18px",
          height: "18px",
          boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
        }}
        onChange={(e) => {
          setExpenseGridData((prev) =>
            prev.map((item) => ({ ...item, add: e.value })),
          );
        }}
        value={expenseGridData.length > 0 && expenseGridData.every((item) => item.add)}
      />
    </div>
  );

  const openSelectExpenseDialog = () => {
    setExpenseGridData(activeExpenses.map((item) => ({ ...item, add: false })));
    setShowSelectExpense(true);
  };

  const closeSelectExpenseDialog = () => {
    setExpenseGridData([]);
    setShowSelectExpense(false);
  };

  const handleSubmit = async () => {
    submitAttemptedRef.current = true;
    setSubmitAttempted(true);

    const errors = [];
    const hasInvalidRows = data.some((item, index) => {
      const rowErrors = [];

      if (
        !item.date ||
        !(item.date instanceof Date) ||
        isNaN(item.date.getTime())
      ) {
        rowErrors.push("Date is required");
      }
      if (!item.details || item.details.trim() === "") {
        rowErrors.push("Details is required");
      }
      if (!item.netAmount || item.netAmount <= 0) {
        rowErrors.push("Value must be greater than 0");
      }
      if (item.vat?.vatCodeId == null) {
        rowErrors.push("VAT Code is required");
      }
      if (!item.nominal?.nominalRef) {
        rowErrors.push("Nominal Ref is required");
      }

      if (rowErrors.length > 0) {
        errors.push({ rowIndex: index + 1, errors: rowErrors });
        return true;
      }
      return false;
    });

    if (hasInvalidRows) {
      setGridErrors(errors);
      return;
    }

    setGridErrors([]);
    setSubmitting(true);

    try {
      const feeEarnerRef = matterDefaults?.matterFeeEarner;

      const requestData = data.map((item) => {
        const netAmount = Number(item.netAmount) || 0;
        const vatAmount = Number(item.vatAmount) || 0;
        return {
          details: item.details?.trim(),
          date: formatDateForAPI(item.date),
          netAmount,
          vatCode: item.vat?.vatCodeId,
          vatRate: resolveVatRate(item, netAmount, vatAmount, item.vat?.rate),
          vatAmount,
          nominalRef: item.nominal?.nominalRef,
          active: true,
          feeEarnerRef,
        };
      });

      await postMatterExpenses(matter?.matterId, requestData);
      toast.success("Expense Request submitted");
      onSubmitSuccess?.();
      cancelEdit?.();
    } catch (submitError) {
      console.error(submitError);
    } finally {
      setSubmitting(false);
    }
  };

  const itemChange = (e) => {
    setData((prev) => {
      const updated = prev.map((item) => {
        if (item.timeId === e.dataItem.timeId) {
          const nextItem = { ...item, [e.field || ""]: e.value };
          if (e.field === "netAmount" && nextItem.vat) {
            const vatAmount = (nextItem.vat.rate / 100) * (e.value || 0);
            nextItem.vatAmount = vatAmount;
            nextItem.gross = (e.value || 0) + vatAmount;
          }
          return nextItem;
        }
        return item;
      });
      return updated;
    });

    if (gridErrors.length > 0) setGridErrors([]);
  };

  useEffect(() => {
    if (activevatsdata.length > 0) {
      setvatdata(activevatsdata.slice());
    }
  }, [activevatsdata]);

  useEffect(() => {
    if (expenseNominals.length > 0) {
      setnominaldata(expenseNominals.slice());
    }
  }, [expenseNominals]);

  useEffect(() => {
    vatDefaultAppliedRef.current = false;
    nominalDefaultAppliedRef.current = false;
  }, [matter?.matterId]);

  useEffect(() => {
    const fetchMatterDefaults = async () => {
      if (!matter?.matterId) return;
      try {
        const response = await getMatterDefaults(matter.matterId);
        setMatterDefaults(response?.data?.data ?? null);
      } catch (loadError) {
        console.error(loadError);
      }
    };

    fetchMatterDefaults();
  }, [matter?.matterId]);

  const resolveDefaultNominal = useCallback(
    (nominalRef) => {
      if (nominalRef == null || nominalRef === "") return null;

      const ref = String(nominalRef);
      const fromExpenseList = expenseNominals.find(
        (item) => String(item.nominalRef) === ref,
      );
      if (fromExpenseList) return fromExpenseList;

      return (
        allNominals.find(
          (item) =>
            item.archived !== true && String(item.nominalRef) === ref,
        ) || null
      );
    },
    [expenseNominals, allNominals],
  );

  useEffect(() => {
    if (
      vatDefaultAppliedRef.current ||
      matterDefaults?.defaultVatCodeId == null ||
      (!allVats.length && !activevatsdata.length)
    ) {
      return;
    }

    const defaultVat =
      findVatById(matterDefaults.defaultVatCodeId, allVats) ||
      findVatById(matterDefaults.defaultVatCodeId, activevatsdata);

    vatDefaultAppliedRef.current = true;

    if (!defaultVat) return;
    setvatvalue(defaultVat);
    setData((prev) => applyVatToAllRows(prev, defaultVat));
    setvatdata((prev) => {
      const list = prev?.length ? prev.slice() : activevatsdata.slice();
      if (
        !list.some(
          (item) =>
            String(item.vatCodeId) === String(defaultVat.vatCodeId),
        )
      ) {
        return [...list, defaultVat];
      }
      return list;
    });
  }, [matterDefaults, allVats, activevatsdata]);

  useEffect(() => {
    if (
      nominalDefaultAppliedRef.current ||
      !matterDefaults ||
      (!expenseNominals.length && !allNominals.length)
    ) {
      return;
    }

    const defaultNominalRef =
      matterDefaults.defaultProfitNominal ?? matter?.defaultProfitCost;
    const defaultNominal = resolveDefaultNominal(defaultNominalRef);

    nominalDefaultAppliedRef.current = true;

    if (!defaultNominal) return;
    setnominal(defaultNominal);
    setData((prev) => applyNominalToAllRows(prev, defaultNominal));
    setnominaldata((prev) => {
      const list = prev?.length ? prev.slice() : expenseNominals.slice();
      if (
        !list.some(
          (item) =>
            String(item.nominalRef) === String(defaultNominal.nominalRef),
        )
      ) {
        return [...list, defaultNominal];
      }
      return list;
    });
  }, [
    matterDefaults,
    matter?.defaultProfitCost,
    expenseNominals,
    allNominals,
    resolveDefaultNominal,
  ]);

  const selectionKey = getSelectionKey(data);

  useEffect(() => {
    if (prevSelectionKeyRef.current === selectionKey) return;
    prevSelectionKeyRef.current = selectionKey;

    const selectedRows = data.filter((row) => row.selected);
    if (!selectedRows.length) return;

    setvatvalue(getSharedVatFromRows(selectedRows));
    setnominal(getSharedNominalFromRows(selectedRows));
  }, [selectionKey, data]);

  const RowSelectionHeaderCell = () => (
    <div className="d-flex align-items-center justify-content-center">
      <Checkbox
        style={{
          width: "18px",
          height: "18px",
          boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
        }}
        onChange={(e) => toggleAllRowsSelected(e.value)}
        value={data.length > 0 && data.every((item) => item.selected)}
      />
    </div>
  );

  const RowSelectionCell = (props) => (
    <td className="p-2 text-center align-middle">
      <Checkbox
        style={{
          width: "18px",
          height: "18px",
          boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
        }}
        value={props.dataItem.selected === true}
        onChange={(e) => toggleRowSelected(props.dataItem.timeId, e.value)}
      />
    </td>
  );

  const dataWithValidation = data.map((item, index) => {
    const rowIndex = index + 1;
    const rowError = gridErrors.find((err) => err.rowIndex === rowIndex);

    return {
      ...item,
      hasDetailsError:
        submitAttempted &&
        rowError?.errors.some((e) => e.includes("Details")),
      hasValueError:
        submitAttempted && rowError?.errors.some((e) => e.includes("Value")),
      hasDateError:
        submitAttempted && rowError?.errors.some((e) => e.includes("Date")),
      hasVatError:
        submitAttempted && rowError?.errors.some((e) => e.includes("VAT Code")),
      hasNominalError:
        submitAttempted &&
        rowError?.errors.some((e) => e.includes("Nominal Ref")),
    };
  });

  return (
    <>
      <Dialog
        title="Expenses"
        onClose={cancelEdit}
        className="dialog-xl"
      >
        <div
          className="d-flex flex-column h-100 overflow-hidden"
          style={{ minHeight: 0 }}
        >
          <div className="flex-shrink-0 pt-4 pb-3 px-5">
            <div className="d-flex justify-content-end mb-3">
              <Button
                className="common-btn setting-btn"
                onClick={openSelectExpenseDialog}
              >
                Select Expense
              </Button>
            </div>

            <div className="validation border rounded px-3 py-3 shadow-sm">
              <Form
                ignoreModified={true}
                onSubmit={handleSubmit}
                id="expenseRequestForm"
                render={() => (
                  <FormElement id="expenseRequestForm" noValidate>
                    <fieldset>
                      <div className="d-flex flex-wrap align-items-end">
                        <div
                          className="d-flex flex-column mb-3 me-3"
                          style={{ width: "300px" }}
                        >
                          <Label>Vat Code</Label>
                          <ComboBox
                            style={{ width: "300px" }}
                            className="mt-1"
                            data={vatdata || activevatsdata}
                            dataItemKey="vatCodeId"
                            textField="display"
                            value={vatvalue}
                            iconClassName="wa"
                            filterable={true}
                            onFilterChange={filtervat}
                            onChange={onChangevat}
                            placeholder="Search VAT Code"
                            disabled={!hasSelectedRows(data)}
                          />
                        </div>

                        <div
                          className="d-flex flex-column mb-3 me-3"
                          style={{ width: "300px" }}
                        >
                          <Label>Nominal Ref</Label>
                          <ComboBox
                            className="mt-1"
                            style={{ width: "300px" }}
                            data={nominaldata || expenseNominals}
                            dataItemKey="nominalRef"
                            iconClassName="wa"
                            textField="display"
                            filterable={true}
                            onFilterChange={filternominal}
                            value={nominal}
                            onChange={onChangenominal}
                            placeholder="Search Nominal Ref"
                            disabled={!hasSelectedRows(data)}
                          />
                        </div>
                      </div>
                    </fieldset>
                  </FormElement>
                )}
              />
            </div>
          </div>

          <div
            className="flex-grow-1 d-flex flex-column px-5 pb-3 mt-2"
            style={{ minHeight: 0 }}
          >
            <Grid
              data={dataWithValidation}
              className="grid-header-sm"
              style={{ height: "100%", minHeight: "180px" }}
              editField="inEdit"
              onItemChange={itemChange}
              dataItemKey="timeId"
            >
              <Column
                field="selected"
                title=" "
                width="50px"
                headerCell={RowSelectionHeaderCell}
                cell={RowSelectionCell}
              />
              <Column
                field="date"
                title="Date"
                format="{0:d}"
                width="140px"
                headerClassName="ps-3"
                cell={DateCell}
              />
              <Column
                field="details"
                title="Details"
                width="200px"
                cell={DetailsCell}
              />
              <Column
                field="netAmount"
                editor="numeric"
                title="Value"
                width="140px"
                cell={ValueCellComponent}
                className="p-2"
              />
              <Column
                field="vat"
                title="Vat Code"
                width="100px"
                cell={VatCodeDisplayCell}
              />
              <Column
                field="nominal"
                title="Nominal Ref"
                width="120px"
                cell={NominalDisplayCell}
              />

              <Column
                field="vatAmount"
                title="Vat"
                editable={false}
                cell={AmountFormatCell}
                className="p-2"
                headerClassName="grid-header-center"
              />
              <Column
                field="gross"
                title="Gross"
                editable={false}
                cell={AmountFormatCell}
                className="p-2"
                headerClassName="grid-header-center"
              />
              <Column cell={DeleteCommandCell} title="" width="50px" />
            </Grid>

            <div className="flex-shrink-0">
              <Button
                title="Add new"
                className="common-btn thirdButton"
                style={{ marginTop: "10px" }}
                onClick={addNew}
              >
                Add New Line
              </Button>
            </div>
          </div>
        </div>

        <DialogActionsBar>
          <div className="dialog-footer w-100 d-flex justify-content-end">
            <Button
              type="button"
              className="common-btn bgBtnSecondary border-0 me-3"
              onClick={cancelEdit}
            >
              Cancel
            </Button>
            <SubmitButton
              type="submit"
              form="expenseRequestForm"
              className="me-4"
              isLoading={submitting}
              onClick={() => {
                submitAttemptedRef.current = true;
                setSubmitAttempted(true);
              }}
            >
              Request
            </SubmitButton>
          </div>
        </DialogActionsBar>
      </Dialog>

      {showSelectExpense && (
        <Dialog
          title="Select Expense"
          onClose={closeSelectExpenseDialog}
          className="dialog-sm"
        >
          <div className="d-flex flex-column px-4 pt-3 pb-2">
            <Grid
              data={expenseGridData}
              dataItemKey="expenseId"
              className="grid-header-sm"
              style={{ maxHeight: "320px" }}
            >
              <Column
                field="add"
                title=" "
                width="90px"
                headerCell={ExpenseSelectionHeaderCell}
                cell={(cellProps) => (
                  <td className="text-center align-middle">
                    <Checkbox
                      style={{
                        width: "18px",
                        height: "18px",
                        boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
                      }}
                      value={cellProps.dataItem.add === true}
                      onChange={(e) => {
                        expenseGridItemChange({
                          dataItem: cellProps.dataItem,
                          value: e.value,
                        });
                      }}
                    />
                  </td>
                )}
              />
              <Column field="details" title="Details" />
              <Column field="nominalRef" title="Nominal" width="100px" />
              <Column
                field="netAmount"
                title="Value"
                width="100px"
                cell={AmountFormatCell}
                headerClassName="grid-header-center"
              />
            </Grid>
          </div>
          <DialogActionsBar>
            <div className="dialog-footer w-100 d-flex justify-content-end">
              <Button
                className="common-btn bgBtnSecondary border-0 me-3"
                onClick={closeSelectExpenseDialog}
              >
                Cancel
              </Button>
              <Button
                className="common-btn me-3"
                disabled={getSelectedExpenseCount() === 0}
                onClick={applySelectedExpenseTemplates}
              >
                Add
              </Button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};

export default ExpenseRequest;
